import { createTheme } from '@mui/material/styles';

const themeConfig = {
    palette: {
        text: {
            primary: 'rgba(0, 0, 0, 0.87)',
            secondary: 'rgba(0, 0, 0, 0.6)',
            tertiary: 'rgba(0, 0, 0, 0.38)',
        },
        primary: {
            light: '#BCC9CE',
            main: '#1F4C5C',
            dark: '#173C4A',
        },
        secondary: {
            light: '#EBF6F0',
            main: '#C7E5D4',
            dark: '#AED8C0',
        },
        tertiary: {
            light: '#FBFCF1',
            main: '#F3F7D7',
            dark: '#EDF2C4',
        },
        error: {
            main: '#B00020',
        },
        warning: {
            main: '#ed6c02',
        },
        success: {
            main: '#0B8D00',
        },
        divider: 'rgba(0, 0, 0, 0.12)',
        action: {
            hover: 'rgba(0, 0, 0, 0.04)',
            highlight: '#F7FBF9',
            disabledBackground: 'rgba(0, 0, 0, 0.14)',
            disabled: 'rgba(0, 0, 0, 0.38)',
        },
        light: {
            main: '#fff',
        },
    },
    typography: {
        fontSize: 16,
        fontFamily: 'Rubik, sans-serif',
        h1: {
            fontFamily: 'Rubik, sans-serif',
            fontStyle: 'normal',
            fontWeight: 300,
            fontSize: '6rem', //96px
            lineHeight: '112px',
            letterSpacing: '-1.5px',
        },
        h2: {
            fontFamily: 'Rubik, sans-serif',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '3.75rem', //60px
            lineHeight: '72px',
            letterSpacing: '-0.5px',
        },
        h3: {
            fontFamily: 'Rubik, sans-serif',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '3rem', //48px
            lineHeight: '56px',
            letterSpacing: '0px',
        },
        h4: {
            fontFamily: 'Rubik, sans-serif',
            fontWeight: 400,
            fontSize: '2.125rem', //34px
            lineHeight: '40px',
            letterSpacing: '-0.25px',
        },
        h5: {
            fontFamily: 'Rubik, sans-serif',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '1.5rem', //28px
            lineHeight: '28px',
            letterSpacing: '0px',
        },
        h6: {
            fontFamily: 'Rubik, sans-serif',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '1.25rem', //48px
            lineHeight: '24px',
            letterSpacing: '0.15px',
        },
        title1: {
            fontFamily: 'Rubik, sans-serif',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '1.5rem', //24px
            lineHeight: '28px',
            letterSpacing: '-0.5px',
        },
        subtitle1: {
            fontFamily: 'Rubik, sans-serif',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '1rem', //16px
            lineHeight: '24px',
            letterSpacing: '0.15px',
        },
        subtitle2: {
            fontFamily: 'Rubik, sans-serif',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '0.875rem', //14px
            lineHeight: '16px',
            letterSpacing: '0.1px',
        },
        body1: {
            fontFamily: 'Rubik, sans-serif',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '1rem', //16px
            lineHeight: '24px',
            letterSpacing: '0.5px',
        },
        body2: {
            fontFamily: 'Rubik, sans-serif',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '0.875rem', //14px
            lineHeight: '16px',
            letterSpacing: '0.25px',
        },
        caption: {
            fontFamily: 'Rubik, sans-serif',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '0.75rem', //12px
            lineHeight: '14px',
            letterSpacing: '0.4px',
        },
    },
    shape: {
        borderRadius: 8,
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 1024,
            lg: 1280,
            xl: 1680,
        },
    },
    components: {
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    fontSize: '1rem',
                },
                fontSizeMedium: {
                    fontSize: '1.5rem',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    letterSpacing: '0.15px',
                },
                adornedStart: {
                    fontFamily: 'Rubik, sans-serif',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                elevation1: {
                    boxShadow: 'none',
                    border: '1px solid var(--divider)',
                    backgroundImage: 'none',
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    '& .MuiTableRow-root:first-of-type > .MuiTableCell-root': {
                        backgroundColor: 'var(--action-hover)',
                    },
                    '& .MuiTableRow-root:first-of-type > .MuiTableCell-root:first-of-type': {
                        borderTopLeftRadius: 8,
                    },
                    '& .MuiTableRow-root:first-of-type > .MuiTableCell-root:last-of-type': {
                        borderTopRightRadius: 8,
                    },
                    '& .MuiTableRow-root:last-of-type > .MuiTableCell-root:first-of-type': {
                        borderBottomLeftRadius: 8,
                    },
                    '& .MuiTableRow-root:last-of-type > .MuiTableCell-root:last-of-type': {
                        borderBottomRightRadius: 8,
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    lineHeight: '1.5rem',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontFamily: 'Rubik, sans-serif',
                },
            },
        },
    },
};

const theme = createTheme(themeConfig);

const toKebab = str => str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

const flattenPalette = (obj, prefix = '') =>
    Object.entries(obj).reduce((vars, [key, value]) => {
        const varName = prefix ? `${prefix}-${toKebab(key)}` : toKebab(key);
        if (typeof value === 'string') {
            vars[`--${varName}`] = value;
        } else if (typeof value === 'object') {
            Object.assign(vars, flattenPalette(value, varName));
        }
        return vars;
    }, {});

export const cssVariables = {
    ':root': {
        ...flattenPalette(themeConfig.palette),
        '--header-height': '72px',
        '--header-height-mobile': '60px',
        '--page-title-height': '0px',
        // AI accent — shared look for AI-powered actions
        '--ai-gradient-start': '#6b4dff',
        '--ai-gradient-mid': '#a64dff',
        '--ai-gradient-end': '#ff4dac',
        '--ai-gradient':
            'linear-gradient(100deg, var(--ai-gradient-start) 0%, var(--ai-gradient-mid) 55%, var(--ai-gradient-end) 100%)',
        '--ai-glow': '0 0 22px rgba(166, 77, 255, 0.55)',
    },
};

export default theme;
